import React, { ReactElement, useMemo } from 'react'
import { highlightInvalidFields, sanitizeAddress } from '../../util/Util'
import { PrimaryButton } from '../../components/Button'
import Dropdown, { DropdownItem } from '../../components/Dropdown'
import { InputLabel, TextAreaWithLabel, TextInputWithLabel } from '../../components/Input'
import PageHeader from '../../components/PageHeader'
import { AccountServicesType, BusinessActivityPeriods, BusinessTypes, BusinessBasics as State } from '../../state/Radom'
import { Industry } from './BusinessIndustry'
import SearchMultiselect from '../../components/SearchMultiselect'
import Tooltip from '../../components/Tooltip'
import { VerificationRequirementError } from '../../components/VerificationRequirementError'

interface IProps {
  state: State
  isSubmitting: boolean
  onUpdate: (value: any) => void
  onSubmit: () => void
  getError: (fieldId: string) => string | undefined
  accountServices: AccountServicesType
}

const BusinessBasics = ({
  state, isSubmitting, onUpdate, onSubmit, getError,
  accountServices
}: IProps): ReactElement => {
  const isValid = useMemo(() => {
    return !!state.name &&
      !!state.description &&
      !!state.website &&
      !!state.email &&
      (accountServices === AccountServicesType.crypto_only ||
        (!!state.businessActivityPeriod && !!state.businessRegistrationNumber)) &&
      (state.businessIndustry?.length ?? 0) > 0
  }, [state, accountServices])

  const businessTypes = useMemo(
    () => Object.entries(BusinessTypes).map(([key, value]) => (
      <DropdownItem key={key} onClick={() => {
        onUpdate({ businessType: key })
      }}>
        {value}
      </DropdownItem>
    )), [])

  const businessActivityPeriods = useMemo(
    () => Object.entries(BusinessActivityPeriods).map(([key, value]) => (
      <DropdownItem key={key} onClick={() => {
        onUpdate({ businessActivityPeriod: key })
      }}>
        {value}
      </DropdownItem>
    )), [])
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, fontSize: 14, paddingBottom: 40 }}>
      <div style={{ marginBottom: 20 }}>
        <PageHeader title="Begin with the basics" subTitle="Let's gather essential information about your business" />
      </div>
      <TextInputWithLabel
        required
        label="Legal business name"
        tooltip="Registered name of your business"
        placeholder="Enter your business name"
        errorMessage={getError('name')}
        value={state.name}
        onChange={(event) => {
          const eventTarget = event.target as HTMLInputElement
          const sanitizedValue = sanitizeAddress(eventTarget.value)
          onUpdate({ name: sanitizedValue })
        }}
      />
      <TextAreaWithLabel
        required
        label="Business description"
        errorMessage={getError('description')}
        placeholder="Describe your business"
        value={state.description}
        onChange={(event) => {
          const eventTarget = event.target as HTMLTextAreaElement
          const sanitizedValue = sanitizeAddress(eventTarget.value)
          onUpdate({ description: sanitizedValue })
        }}
      />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <InputLabel>Business type</InputLabel>
          <Tooltip message='Select the legal structure of your business' />
        </div>
        <VerificationRequirementError message={getError('businessType')} />
        <Dropdown
          key={state.businessType}
          selectedContent={BusinessTypes[state.businessType] || 'Select business type'}
          dropdownContent={businessTypes}
        />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <InputLabel>
            Business industry
          </InputLabel>
          <Tooltip message='If your business industry is not on the list, please select Unclassified.' />
        </div>
        <VerificationRequirementError message={getError('businessIndustry')} />
        <SearchMultiselect
          dropdownListItemStyle={{
            maxWidth: 500
          }}
          placeholder="Select business industry"
          dropdownPlaceholder="No industries found"
          itemArray={
            Object.values(Industry)
              .filter(t => typeof t === 'string')
              .map(k => ({
                key: k,
                value: Industry[k]
              }))
          }
          selectedItems={
            state.businessIndustry
              ? Object.values(Industry)
                .filter(t => typeof t === 'string')
                .map(k => ({
                  key: k,
                  value: Industry[k]
                }))
                .filter(d => state.businessIndustry.includes(d.value.toString()))
              : []
          }
          setSelectedItems={(items) => {
            onUpdate({ businessIndustry: items.map(i => i.value.toString()) })
          }}
          showMultiSelectIndicator={true}
          keyExtractor={(country) => country?.key ?? ''}
          labelExtractor={(country) => country?.key}
        />
      </div>
      {accountServices === AccountServicesType.crypto_and_fiat && <TextInputWithLabel
        required
        label="Business registration number"
        tooltip="Registration number assigned to your business by the company register"
        placeholder="Enter your business registration number"
        value={state.businessRegistrationNumber}
        errorMessage={getError('businessRegistrationNumber')}
        onChange={(event) => {
          const eventTarget = event.target as HTMLInputElement
          onUpdate({ businessRegistrationNumber: eventTarget.value })
        }}
      />}
      {accountServices === AccountServicesType.crypto_and_fiat && <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <InputLabel>Business activity period</InputLabel>
          <Tooltip message='Select business activity period' />
        </div>
        <VerificationRequirementError message={getError('businessActivityPeriod')} />
        <Dropdown
          key={state.businessActivityPeriod}
          selectedContent={BusinessActivityPeriods[state.businessActivityPeriod] || 'Select business activity period'}
          dropdownContent={businessActivityPeriods}
        />
      </div>}
      <TextInputWithLabel
        required
        label="Business website"
        tooltip="Public website for your business"
        placeholder="Enter your business website"
        value={state.website}
        onChange={(event) => {
          const eventTarget = event.target as HTMLInputElement
          onUpdate({ website: eventTarget.value })
        }}
        errorMessage={getError('website')}
      />
      <TextInputWithLabel
        required
        label="Business email"
        tooltip="Where we can reach you for verification updates"
        placeholder="Enter your business email"
        value={state.email}
        errorMessage={getError('email')}
        onChange={(event) => {
          const eventTarget = event.target as HTMLInputElement
          onUpdate({ email: eventTarget.value })
        }}
      />
      <PrimaryButton
        disabled={isSubmitting}
        isLoading={isSubmitting}
        onClick={() => {
          if (isValid) {
            onSubmit()
          } else {
            highlightInvalidFields()
          }
        }}>
        Continue
      </PrimaryButton>
    </div>
  )
}

export default BusinessBasics
